/**
 * Admin — User Management (/admin/users)
 *
 * Full CRUD: list all users, add manually (bypass Stripe),
 * edit subscription details, delete. Admin-only.
 */

import { useEffect, useState } from 'react';
import { Link, useLocation, useRoute } from 'wouter';
import { AppLayout } from '@/layouts/AppLayout';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';

// ── Types ─────────────────────────────────────────────────────────────────────

interface AdminUser {
  id: string;
  email: string;
  name: string | null;
  subscription_status: string;
  subscription_expires_at: string | null;
  stripe_customer_id: string | null;
  cohort: string | null;
  is_admin: number;
  survey_completed: number;
  created_at: string;
  deletion_scheduled_at: string | null;
}

type SubscriptionStatus = 'active' | 'inactive' | 'past_due' | 'cancelled';

const STATUS_STYLES: Record<string, string> = {
  active:    'bg-green-100 text-green-800',
  inactive:  'bg-gray-100 text-gray-500',
  past_due:  'bg-amber-100 text-amber-800',
  cancelled: 'bg-red-100 text-red-700',
};

function statusLabel(s: string): string {
  return s === 'past_due' ? 'Past Due' : s.charAt(0).toUpperCase() + s.slice(1);
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ── List page ─────────────────────────────────────────────────────────────────

function AdminUsersContent(): React.ReactElement {
  const [, navigate] = useLocation();
  const { user } = useAuth();
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (user && !user.isAdmin) navigate('/dashboard');
  }, [user, navigate]);

  const load = async (): Promise<void> => {
    try {
      const result = await api.get<{ users: AdminUser[] }>('/api/content/users');
      setUsers(result.users);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return;
      setError(err instanceof Error ? err.message : 'Failed to load users.');
    }
  };

  useEffect(() => { void load(); }, []);

  const deleteUser = async (id: string, name: string | null, email: string): Promise<void> => {
    if (!window.confirm(`Delete ${name ?? email}? This permanently removes their account and cannot be undone.`)) return;
    setBusy(id);
    try {
      await api.delete(`/api/content/users/${id}`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user.');
    } finally {
      setBusy(null);
    }
  };

  const filtered = users?.filter(u =>
    search === '' ||
    u.email.toLowerCase().includes(search.toLowerCase()) ||
    (u.name ?? '').toLowerCase().includes(search.toLowerCase())
  ) ?? [];

  const counts = users ? {
    total: users.length,
    active: users.filter(u => u.subscription_status === 'active').length,
    inactive: users.filter(u => u.subscription_status !== 'active').length,
  } : null;

  return (
    <div className="stitch-font-body text-[var(--stitch-on-surface)]">

      {/* Header */}
      <div className="flex items-start justify-between mb-8 gap-4">
        <div>
          <h1 className="stitch-font-display text-[30px] md:text-[34px] font-bold text-[var(--stitch-primary)] tracking-tight">
            Members
          </h1>
          <p className="stitch-font-body text-[14px] text-[var(--stitch-on-surface-variant)] mt-1">
            Admin — manage practitioner accounts and subscriptions.
          </p>
        </div>
        <Link
          href="/admin/users/new"
          className="inline-flex items-center gap-2 px-6 py-3 bg-[var(--stitch-primary)] text-[var(--stitch-on-primary)] rounded-full stitch-font-label text-[13px] uppercase tracking-widest font-bold hover:opacity-90 transition-opacity shrink-0"
        >
          <span className="material-symbols-outlined text-[18px]">person_add</span>
          Add member
        </Link>
      </div>

      {/* Stats */}
      {counts && (
        <div className="grid grid-cols-3 gap-4 mb-8">
          {[
            { label: 'Total members', value: counts.total, icon: 'group' },
            { label: 'Active', value: counts.active, icon: 'verified', color: 'text-green-600' },
            { label: 'Inactive / lapsed', value: counts.inactive, icon: 'person_off', color: 'text-[var(--stitch-on-surface-variant)]' },
          ].map(s => (
            <div key={s.label} className="bg-[var(--stitch-surface-container-lowest)] border border-[var(--stitch-outline-variant)] rounded-xl p-5 flex items-center gap-4">
              <span className={`material-symbols-outlined text-[32px] ${s.color ?? 'text-[var(--stitch-primary)]'}`}>{s.icon}</span>
              <div>
                <p className="stitch-font-display text-[28px] text-[var(--stitch-on-surface)] leading-none">{s.value}</p>
                <p className="stitch-font-label text-[11px] text-[var(--stitch-on-surface-variant)] uppercase tracking-wider mt-1">{s.label}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Search */}
      <div className="relative mb-6">
        <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-[var(--stitch-on-surface-variant)] text-[20px]">search</span>
        <input
          type="text"
          placeholder="Search by name or email…"
          value={search}
          onChange={e => { setSearch(e.target.value); }}
          className="w-full pl-10 pr-4 py-2.5 bg-[var(--stitch-surface-container-lowest)] border border-[var(--stitch-outline-variant)] rounded-lg focus:ring-1 focus:ring-[var(--stitch-primary)] outline-none stitch-font-body text-[14px]"
        />
      </div>

      {error && (
        <div role="alert" className="mb-6 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {!users && !error && (
        <div className="flex items-center justify-center py-24">
          <span className="material-symbols-outlined animate-spin text-[var(--stitch-primary)] text-4xl">progress_activity</span>
        </div>
      )}

      {users && filtered.length === 0 && (
        <div className="text-center py-16 text-[var(--stitch-on-surface-variant)]">
          {search ? 'No members match that search.' : 'No members yet.'}
        </div>
      )}

      {filtered.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-[var(--stitch-outline-variant)]">
          <table className="w-full stitch-font-body text-[14px]">
            <thead className="bg-[var(--stitch-surface-container-low)] text-left">
              <tr>
                {['Member', 'Status', 'Expires', 'Cohort', 'Joined', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 stitch-font-label text-[11px] text-[var(--stitch-on-surface-variant)] uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--stitch-outline-variant)]">
              {filtered.map(u => (
                <tr key={u.id} className="bg-[var(--stitch-surface-container-lowest)] hover:bg-[var(--stitch-surface-container-low)] transition-colors">
                  <td className="px-4 py-3">
                    <p className="font-medium text-[var(--stitch-on-surface)] truncate max-w-[220px]">
                      {u.name ?? <span className="text-[var(--stitch-on-surface-variant)] italic">No name</span>}
                      {u.is_admin ? <span className="ml-2 text-[10px] bg-[var(--stitch-primary)] text-[var(--stitch-on-primary)] px-1.5 py-0.5 rounded-full uppercase tracking-wide">Admin</span> : null}
                      {u.deletion_scheduled_at ? (
                        <span className="ml-2 text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full uppercase tracking-wide" title={`Deletion on ${formatDate(u.deletion_scheduled_at)}`}>
                          Pending deletion · {Math.max(0, Math.ceil((new Date(u.deletion_scheduled_at).getTime() - Date.now()) / 86_400_000))}d
                        </span>
                      ) : null}
                    </p>
                    <p className="text-[12px] text-[var(--stitch-on-surface-variant)]">{u.email}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full stitch-font-label text-[11px] font-medium ${STATUS_STYLES[u.subscription_status] ?? 'bg-gray-100 text-gray-500'}`}>
                      {statusLabel(u.subscription_status)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[var(--stitch-on-surface-variant)] text-[13px]">
                    {formatDate(u.subscription_expires_at)}
                  </td>
                  <td className="px-4 py-3 text-[var(--stitch-on-surface-variant)] text-[13px]">
                    {u.cohort ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-[var(--stitch-on-surface-variant)] text-[13px]">
                    {formatDate(u.created_at)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <Link
                        href={`/admin/users/${u.id}/edit`}
                        className="stitch-font-label text-[12px] text-[var(--stitch-primary)] uppercase tracking-wider hover:underline"
                      >
                        Edit
                      </Link>
                      {u.stripe_customer_id && (
                        <a
                          href={`https://dashboard.stripe.com/customers/${u.stripe_customer_id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="stitch-font-label text-[12px] text-[var(--stitch-on-surface-variant)] uppercase tracking-wider hover:underline"
                          title="Open in Stripe dashboard"
                        >
                          Stripe ↗
                        </a>
                      )}
                      <button
                        type="button"
                        disabled={busy === u.id}
                        onClick={() => { void deleteUser(u.id, u.name, u.email); }}
                        className="stitch-font-label text-[12px] text-red-500 uppercase tracking-wider hover:text-red-700 transition-colors disabled:opacity-40"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function AdminUsersPage(): React.ReactElement {
  return (
    <AppLayout currentRoute="/admin/users">
      <AdminUsersContent />
    </AppLayout>
  );
}

// ── Create / Edit form ────────────────────────────────────────────────────────

interface UserFormState {
  email: string;
  name: string;
  subscription_status: SubscriptionStatus;
  subscription_expires_at: string;
  cohort: string;
  is_admin: boolean;
}

const EMPTY_FORM: UserFormState = {
  email: '',
  name: '',
  subscription_status: 'active',
  subscription_expires_at: '',
  cohort: '',
  is_admin: false,
};

function AdminUserFormContent({ editId }: { editId?: string }): React.ReactElement {
  const [, navigate] = useLocation();
  const { user } = useAuth();
  const [form, setForm] = useState<UserFormState>(EMPTY_FORM);
  const [loading, setLoading] = useState(Boolean(editId));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user && !user.isAdmin) navigate('/dashboard');
  }, [user, navigate]);

  useEffect(() => {
    if (!editId) return;
    void (async () => {
      try {
        const data = await api.get<AdminUser>(`/api/content/users/${editId}`);
        setForm({
          email: data.email,
          name: data.name ?? '',
          subscription_status: data.subscription_status as SubscriptionStatus,
          subscription_expires_at: data.subscription_expires_at?.slice(0, 10) ?? '',
          cohort: data.cohort ?? '',
          is_admin: Boolean(data.is_admin),
        });
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return;
        setError(err instanceof Error ? err.message : 'Failed to load user.');
      } finally {
        setLoading(false);
      }
    })();
  }, [editId]);

  const setField = <K extends keyof UserFormState>(field: K, value: UserFormState[K]): void => {
    setForm(f => ({ ...f, [field]: value }));
  };

  const save = async (): Promise<void> => {
    if (!form.email.trim()) { setError('Email is required.'); return; }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        email: form.email.trim().toLowerCase(),
        name: form.name.trim() || undefined,
        subscription_status: form.subscription_status,
        subscription_expires_at: form.subscription_expires_at || null,
        cohort: form.cohort.trim() || null,
        is_admin: form.is_admin,
      };
      if (editId) {
        await api.put(`/api/content/users/${editId}`, payload);
      } else {
        await api.post('/api/content/users', payload);
      }
      navigate('/admin/users');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <span className="material-symbols-outlined animate-spin text-[var(--stitch-primary)] text-4xl">progress_activity</span>
      </div>
    );
  }

  return (
    <div className="stitch-font-body text-[var(--stitch-on-surface)] max-w-lg">
      <div className="mb-6">
        <Link href="/admin/users" className="stitch-font-label text-[13px] text-[var(--stitch-primary)] no-underline inline-flex items-center gap-1">
          <span className="material-symbols-outlined text-[18px]">arrow_back</span> All members
        </Link>
      </div>

      <h1 className="stitch-font-display text-[30px] md:text-[34px] font-bold text-[var(--stitch-primary)] tracking-tight mb-8">
        {editId ? 'Edit member' : 'Add member'}
      </h1>

      {error && (
        <div role="alert" className="mb-6 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      <div className="space-y-5">
        <Field label="Email address">
          <input
            type="email"
            value={form.email}
            onChange={e => { setField('email', e.target.value); }}
            placeholder="practitioner@example.com"
            className={inputCls}
            disabled={Boolean(editId)}
          />
          {editId && <p className="stitch-font-body text-[11px] text-[var(--stitch-on-surface-variant)]">Email cannot be changed after account creation.</p>}
        </Field>

        <Field label="Full name">
          <input
            type="text"
            value={form.name}
            onChange={e => { setField('name', e.target.value); }}
            placeholder="e.g. Sarah Johnson"
            className={inputCls}
          />
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Subscription status">
            <select
              value={form.subscription_status}
              onChange={e => { setField('subscription_status', e.target.value as SubscriptionStatus); }}
              className={inputCls}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="past_due">Past Due</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </Field>

          <Field label="Expires" hint="Leave blank for no expiry">
            <input
              type="date"
              value={form.subscription_expires_at}
              onChange={e => { setField('subscription_expires_at', e.target.value); }}
              className={inputCls}
            />
          </Field>
        </div>

        <Field label="Cohort" hint="e.g. founding, jo-network, organic">
          <input
            type="text"
            value={form.cohort}
            onChange={e => { setField('cohort', e.target.value); }}
            placeholder="optional"
            className={inputCls}
          />
        </Field>

        <div className="flex items-center gap-3 pt-1">
          <input
            type="checkbox"
            id="is_admin"
            checked={form.is_admin}
            onChange={e => { setField('is_admin', e.target.checked); }}
            className="accent-[var(--stitch-primary)] w-4 h-4"
          />
          <label htmlFor="is_admin" className="stitch-font-body text-[14px] cursor-pointer">
            Admin access (can manage modules and members)
          </label>
        </div>

        <div className="flex gap-3 pt-4 border-t border-[var(--stitch-outline-variant)]">
          <button
            type="button"
            onClick={() => { void save(); }}
            disabled={saving}
            className="px-8 py-4 bg-[var(--stitch-primary)] text-[var(--stitch-on-primary)] rounded-full stitch-font-label text-[13px] uppercase tracking-widest font-bold hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving…' : editId ? 'Save changes' : 'Add member'}
          </button>
          <Link
            href="/admin/users"
            className="px-8 py-4 border border-[var(--stitch-outline-variant)] rounded-full stitch-font-label text-[13px] uppercase tracking-widest text-[var(--stitch-on-surface-variant)] hover:bg-[var(--stitch-surface-container-low)] transition-colors"
          >
            Cancel
          </Link>
        </div>
      </div>
    </div>
  );
}

const inputCls = 'w-full px-3 py-2 bg-[var(--stitch-surface-container-lowest)] border border-[var(--stitch-outline-variant)] rounded-lg focus:ring-1 focus:ring-[var(--stitch-primary)] focus:border-[var(--stitch-primary)] outline-none stitch-font-body text-[14px] text-[var(--stitch-on-surface)]';

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }): React.ReactElement {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="stitch-font-label text-[12px] text-[var(--stitch-on-surface-variant)] uppercase tracking-wider">{label}</label>
      {children}
      {hint && <p className="stitch-font-body text-[11px] text-[var(--stitch-on-surface-variant)]">{hint}</p>}
    </div>
  );
}

export function AdminUserNewPage(): React.ReactElement {
  return (
    <AppLayout currentRoute="/admin/users">
      <AdminUserFormContent />
    </AppLayout>
  );
}

export function AdminUserEditPage(): React.ReactElement {
  const [, params] = useRoute<{ id: string }>('/admin/users/:id/edit');
  const id = params?.id ?? '';
  return (
    <AppLayout currentRoute="/admin/users">
      <AdminUserFormContent editId={id} />
    </AppLayout>
  );
}
